import { useContext, useEffect, useRef, useState } from 'react';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Divider, ListItem, Popover } from '@mui/material';
import { Cookies } from 'react-cookie';
import { backToParent, studentLogout } from 'src/services/auth.service';
import { useSnackbar } from 'notistack';
import { routeURL } from 'src/utils/properties';
import { getParentById } from 'src/services/Parent/parent.service';
import { errorHandler } from 'src/utils/error-handler.utils';
import { getStudentParentById } from 'src/services/Student/student.service';
import { useScreenBreakpoints } from 'src/utils/screenBreakpoints';
import {
  FlexEndGridContainer,
  GridContainer,
  GridItem,
  Text1,
  Text2,
  Text3
} from 'src/components/StyledComponents/Index';
import {
  ButtonText,
  ChildPopoverGridContainer1,
  MyChildListGridItem,
  ParentPopoverGridContainer,
  UserBoxButton,
  UserBoxLabel,
  UserBoxText
} from 'src/components/StyledComponents/Navigation';
import { Parent } from 'src/models/parent.interface';
import { removeStudentFromQueue } from 'src/services/Student/askForHelp.service';
import { LoginType, ParentDashboardType } from 'src/enums';
import { MyAccount } from 'src/components/StyledComponents/StudentLogin';
import { LoggedInUserContext } from 'src/contexts/LoggedInUsersContext';
import { clearUserData, setLocalStorageData } from 'src/utils/functions.utils';
import {
  PopeOverProfileAvatar,
  PopoverStudentProfileParentAvatar,
  ProfileAvatar
} from 'src/components/StyledComponents/Header';

function HeaderUserbox() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isSmallScreen } = useScreenBreakpoints();
  const { contextLoggedInUser, getContextLoggedInUser } =
    useContext(LoggedInUserContext);

  const [parent, setParent] = useState<Parent>();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const ref = useRef<any>(null);

  useEffect(() => {
    getContextLoggedInUser();
  }, []);

  const handleOpen = async (): Promise<void> => {
    setLoading(true);
    if (contextLoggedInUser?.parentId) {
      getParent();
    }
    setOpen(true);
  };

  const getParent = async () => {
    try {
      const response: any = await getStudentParentById(
        contextLoggedInUser.parentId
      );
      setParent(response.parent);
      setLoading(false);
    } catch (error: any) {
      errorHandler(error);
    }
  };

  const backToParentAcc = async (student: any) => {
    try {
      const response: any = await backToParent();
      const parentResponse: any = await getParentById();
      setLocalStorageData({
        ...parentResponse.parent,
        userType: LoginType.PARENT
      });
      enqueueSnackbar(response.data.message, { variant: 'success' });
      navigate(
        `${routeURL.PARENT}${routeURL.DASHBOARD}?tab=${ParentDashboardType.MY_STUDENTS}`
      );
      window.location.reload();
    } catch (error) {
      enqueueSnackbar(errorHandler(error), { variant: 'success' });
    }
  };

  const handleClose = (): void => setOpen(false);

  const handleLogOut = async () => {
    try {
      removeStudentFromQueue();
      const response: any = await studentLogout();
      enqueueSnackbar(response.message, { variant: 'success' });
    } catch (error) {
    } finally {
      clearUserData();
      redirectToLogin();
    }
  };

  const redirectToLogin = () => {
    const loginRoute =
      contextLoggedInUser.userType === LoginType.PARENT
        ? routeURL.PARENT_LOGIN
        : routeURL.STUDENT_LOGIN;
    navigate(loginRoute);
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <ProfileAvatar />
        {!isSmallScreen && (
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {contextLoggedInUser?.firstName}
            </UserBoxLabel>
          </UserBoxText>
        )}
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <ParentPopoverGridContainer>
          <GridItem md={12} xs={12}>
            <ChildPopoverGridContainer1>
              <GridItem md={3} xs={3} margin="auto">
                <PopeOverProfileAvatar />
              </GridItem>
              <GridItem xs={6} margin="auto">
                <GridContainer rowSpacing="20px">
                  <GridItem md={12}>
                    <GridContainer>
                      <GridItem md={12}>
                        <Text1 fontWeight={700}>
                          {contextLoggedInUser?.firstName}
                        </Text1>
                      </GridItem>
                      <GridItem md={12}>
                        <Text3>{contextLoggedInUser?.email}</Text3>
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                  <GridItem md={12}>
                    <MyAccount
                      disableRipple
                      component={RouterLink}
                      to="my-account"
                      onClick={handleClose}
                    >
                      My Account
                    </MyAccount>
                  </GridItem>
                </GridContainer>
              </GridItem>
            </ChildPopoverGridContainer1>
          </GridItem>
          <GridItem md={12} xs={12}>
            <Divider />
          </GridItem>
          {!loading && contextLoggedInUser?.parentId && (
            <ListItem
              button
              onClick={() => {
                backToParentAcc(contextLoggedInUser);
              }}
            >
              <MyChildListGridItem md={12} xs={12}>
                <GridContainer rowSpacing="16px" p={1}>
                  <GridItem md={12}>
                    <Text1 fontWeight={700}>Parent</Text1>
                  </GridItem>
                  <GridItem md={12}>
                    <GridItem md={12} xs={12}>
                      <GridContainer>
                        <GridItem md={2} xs={3} margin="auto">
                          <PopoverStudentProfileParentAvatar
                            src={parent?.profile}
                          />
                        </GridItem>
                        <GridItem md={6} xs={6} margin="auto">
                          <GridContainer>
                            <GridItem md={12}>
                              <Text2>{parent?.firstName}</Text2>
                            </GridItem>
                            <GridItem md={12}>
                              <Text3>{parent?.email}</Text3>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </GridItem>
                </GridContainer>
              </MyChildListGridItem>
            </ListItem>
          )}
          <GridItem md={12} xs={12} pt={2}>
            <FlexEndGridContainer>
              <Button variant="outlined" onClick={handleLogOut}>
                <ButtonText>Log out</ButtonText>
              </Button>
            </FlexEndGridContainer>
          </GridItem>
        </ParentPopoverGridContainer>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
